/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useRef, useState } from "react";
import { MediaBatchPreparationInitServices } from "../../../../../../service/mediaBatchPrepInitServices";
import {
	ComplianceCheckList,
	MediaSterilization,
} from "../../../types/mediaBatchSterilization.types";
import { useTranslation } from "react-i18next";
import { SterilizationDetails } from "../../../types/mediaBatchCommon.types";

interface Props {
	handlePayloadChange: (field: string, value: string) => void;
	sterilizationDetails?: SterilizationDetails;
	sterilizationPayload: MediaSterilization;
	complianceCheckList: ComplianceCheckList[];
	handleChecklistChange: (id: number, checked: boolean) => void;
	isReadOnly: boolean;
	errors: {
		steamIndicator: string;
		file: string;
	};
	setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const MBSComplianceCheckList = ({
	handlePayloadChange,
	sterilizationDetails,
	sterilizationPayload,
	complianceCheckList,
	handleChecklistChange,
	isReadOnly,
	errors,
	setErrors
	
}: Props) => {
	const { t } = useTranslation();

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [selectedFileURL, setSelectedFileURL] = useState<string | null>(null);
	const [cloxImage, setCloxImage] = useState<File | null>(null);
	const [uploadError, setUploadError] = useState("");
	const [cloxRemarks, setCloxRemarks] = useState<string>("");
	const [isUploading, setIsUploading] = useState(false);
	const cloxPassed: "PASS" | "FAIL" | "" = sterilizationPayload.steam_clox_indicator_result;
	
	useEffect(() => {
		if (!sterilizationDetails) return;

		handlePayloadChange(
			"steam_clox_indicator_result",
			sterilizationDetails.steam_clox_indicator_result ?? ""
		);
	}, [sterilizationDetails]);	

	const handleClick = () => {
		fileInputRef.current?.click(); // Opens the file dialog
	};

	const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setUploadError("");
		setIsUploading(true); // 👈 lock
		try {
			const response =
				await MediaBatchPreparationInitServices.uploadFile(file);

			if (response?.status && response?.statusCode === 200) {
				handlePayloadChange("file_id", response?.file_id);
				setErrors((prev: any) => ({ ...prev, file: "" }));
				const objectUrl = URL.createObjectURL(file);
				setSelectedFileURL(objectUrl);
				setCloxImage(file);
			}
		} catch (error) {
			setUploadError("File upload failed");
		} finally {
			setIsUploading(false); // 👈 always unlock, even on error
		}
	};

	const toggleCloxPassed = (value: "PASS" | "FAIL") => {
		handlePayloadChange("steam_clox_indicator_result", value);
		setErrors((prev: any) => ({ ...prev, steamIndicator: "" }));
	};

	useEffect(() => {
		return () => {
			if (selectedFileURL) URL.revokeObjectURL(selectedFileURL); // Cleanup
		};
	}, [selectedFileURL]);

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
			<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
				<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
					04
				</div>

				<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
					{t("mediaBatchInit.sterilization.safetyAndComplianceCheck")}
				</h3>
			</div>

			<div className="p-6">
				<div className="grid grid-cols-12 gap-4">
					{/* General Checks */}

					<div className="col-span-12 md:col-span-5 space-y-3">
						{complianceCheckList.map((check) => {
							const isChecked = sterilizationPayload.checklist_data.some(
								(item) =>
									item.id === check.id && item.status === "PASS",
							);

							return (
								<label
									key={check.id}
									className={`flex items-center gap-4 p-4 border border-slate-100 rounded-xl transition-all group ${
										isReadOnly
											? "bg-slate-100 opacity-70 cursor-not-allowed"
											: "bg-slate-50/30 hover:bg-slate-50 cursor-pointer"
									}`}
									onClick={() => {
										if (!isReadOnly) {
											handleChecklistChange(check.id, !isChecked);
										}
									}}
								>
									<div
										onClick={() =>
											handleChecklistChange(check.id, !isChecked)
										}
										className="w-5 h-5 rounded border-2 border-slate-200 flex items-center justify-center bg-white group-hover:border-orange-500 transition-colors"
									>
										<input
											type="checkbox"
											checked={isChecked}
											disabled={isReadOnly}
										/>

										{/*<div

										className={`w-2.5 h-2.5 rounded-sm bg-orange-500 transition-transform ${

										isChecked ? "scale-100" : "scale-0"

										}`}

										/>*/}
									</div>

									<span className="text-[10px] font-black text-slate-500 uppercase tracking-tight">
										{check.checklist_name}
									</span>
								</label>
							);
						})}
					</div>

					{/* Steam Clox Indicator Section */}

					<div className="col-span-12 md:col-span-7">
						<div
							className={`p-6 rounded-2xl border-2 transition-all ${cloxPassed === "PASS" ? "border-red-200 bg-red-50/30" : "border-slate-100 bg-slate-50/50"}`}
						>
							<div className="flex flex-col md:flex-row gap-6">
								<div className="flex-1 space-y-4">
									<div className="flex items-center justify-between">
										<h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest">
											{t(
												"mediaBatchInit.sterilization.steamCloxIndicatorResult",
											)}  <span className="text-red-500">*</span>
										</h4>

										<div
											className={`flex rounded-lg p-1 border border-slate-200 ${
												isReadOnly ? "bg-slate-100 opacity-70" : "bg-white"
											}`}
										>
											<button
												disabled={isReadOnly}
												onClick={() => toggleCloxPassed("PASS")}
												className={`px-4 py-1.5 rounded-md text-[9px] font-black uppercase tracking-wider transition-all border-none ${cloxPassed === "PASS" ? "bg-emerald-500 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
											>
												{t("mediaBatchInit.sterilization.pass")}
											</button>

											<button
												disabled={isReadOnly}
												onClick={() => toggleCloxPassed("FAIL")}
												className={`px-4 py-1.5 rounded-md text-[9px] font-black uppercase tracking-wider transition-all border-none ${cloxPassed === "FAIL" ? "bg-red-500 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
											>
												{t("mediaBatchInit.sterilization.pass")}
											</button>
										</div>
									</div>
									{!isReadOnly && errors.steamIndicator && <p className="text-red-500 text-[11px]">{errors.steamIndicator}</p>}

									<div className="flex items-center gap-4">
										<div className="flex-1">
											<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
												{t(
													"mediaBatchInit.sterilization.indicatorEvidenceUpload",
												)}{" "}
												<span className="text-red-500">*</span>
											</p>

											{uploadError && (
												<div className="flex items-center justify-center gap-2 mt-4">
													<span className="material-symbols-outlined text-red-500 text-[18px]">
														{t("gptTest.icons.error")}
													</span>

													<p className="text-[11px] font-bold text-red-500">
														{uploadError}
													</p>
												</div>
											)}

											<input
												type="file"
												ref={fileInputRef}
												onChange={handleImageUpload}
												accept="image/*"
												className="hidden"
												disabled={isReadOnly}
											/>

											<button
												onClick={() => handleClick()}
												disabled={isUploading || isReadOnly} // 👈 disables click
												className={`w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl transition-all
												${
													isReadOnly
														? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-70"
														: isUploading
															? "border-orange-300 bg-orange-50 text-orange-400 cursor-not-allowed animate-pulse"
															: "border-slate-200 bg-white hover:border-orange-500 text-slate-400 hover:text-orange-500"
												}`}
											>
												<span className="material-symbols-outlined text-sm">
													{isUploading
														? "hourglass_top"
														: "photo_camera"}
												</span>

												<span className="text-[10px] font-black uppercase tracking-widest">
													{isUploading
														? "Uploading..."
														: cloxImage
															? "Change Image"
															: "Upload Indicator Image"}
												</span>
											</button>
											{!isReadOnly && errors.file && <p className="text-red-500 text-[11px]">{errors.file}</p>}
										</div>

										{selectedFileURL && (
											<div className="w-24 h-24 rounded-xl border-2 border-white shadow-md overflow-hidden bg-white shrink-0">
												<img
													src={selectedFileURL}
													alt="Indicator"
													className="w-full h-full object-cover"
												/>
											</div>
										)}
									</div>
								</div>

								{cloxPassed === "FAIL" && (
									<div className="flex-1 animate-in slide-in-from-right-2 duration-300">
										<div className="space-y-2">
											<label className="text-[9px] font-black text-red-600 uppercase tracking-widest flex items-center gap-2">
												<span className="material-symbols-outlined text-sm">
													warning
												</span>
												{t(
													"mediaBatchInit.sterilization.indicatorDeviationRemarks",
												)}{" "}
												*
											</label>

											<textarea
												value={cloxRemarks}
												onChange={(e) =>
													setCloxRemarks(e.target.value)
												}
												placeholder="State reason for indicator failure..."
												className="w-full bg-white border border-red-100 rounded-xl p-3 text-[10px] font-medium text-slate-600 outline-none min-h-[100px] resize-none focus:ring-2 focus:ring-red-500/10"
											></textarea>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MBSComplianceCheckList;
